import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import useSeo from "@/hooks/useSeo";
import { categories, comics, siteMeta } from "@/mocks/comics";

const colorMap: Record<string, string> = {
  primary: "text-primary-600 bg-primary-100",
  accent: "text-accent-700 bg-accent-100",
  secondary: "text-secondary-700 bg-secondary-100",
  foreground: "text-foreground-700 bg-foreground-100",
};

export default function CategoriesPage() {
  useSeo({
    title: "韩漫题材分类 - 韩漫YY榜 恋爱奇幻校园悬疑分类选书",
    description:
      "韩漫YY榜题材分类页面，涵盖恋爱、奇幻、校园、悬疑、都市、职场、热血、治愈等韩漫分类，支持关键词搜索与连载/完结状态筛选，帮你按分类快速定位想看的韩漫。",
    keywords: "韩漫分类,韩漫yy分类,韩漫恋爱,韩漫悬疑,韩漫完结漫画",
    path: "/categories",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "韩漫题材分类",
      description: "按题材浏览韩漫作品，支持搜索与状态筛选。",
      url: `${window.location.origin}/categories`,
      isPartOf: {
        "@type": "WebSite",
        name: siteMeta.name,
        url: window.location.origin,
      },
    },
  });

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"全部" | "连载中" | "已完结">("全部");

  const results = comics.filter((c) => {
    const matchQuery = !query || c.title.includes(query.trim()) || c.category.includes(query.trim());
    const matchStatus = status === "全部" || c.status === status;
    return matchQuery && matchStatus;
  });

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
          <nav aria-label="面包屑" className="text-sm text-foreground-600">
            <ol className="flex flex-wrap items-center gap-2">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li className="text-foreground-400"><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="text-foreground-900">题材分类</li>
            </ol>
          </nav>

          <div className="mt-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">按题材选书</h1>
              <p className="mt-2 max-w-xl text-sm text-foreground-600">
                浏览全部韩漫分类，或用下方搜索框、状态筛选快速定位想看的作品。
              </p>
            </div>
            <div className="relative w-full md:w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索书名或题材…"
                aria-label="搜索韩漫"
                className="w-full rounded-md border border-background-300 bg-background-100 py-2.5 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                className="card-lift group relative overflow-hidden rounded-lg bg-background-100 aspect-[4/5]"
              >
                <img
                  src={c.image}
                  alt={`${c.name} 韩漫分类`}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute inset-x-0 bottom-0 p-4 text-background-50">
                  <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${colorMap[c.color]}`}>
                    {c.en}
                  </span>
                  <h2 className="mt-2 font-heading text-xl">{c.name}</h2>
                  <p className="mt-1 text-xs text-background-100">{c.count} · 查看作品</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 border-t border-background-200/70 pt-8">
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
              <h2 className="font-heading text-2xl text-foreground-950">
                {query || status !== "全部" ? "筛选结果" : "全部作品"}
              </h2>
              <div className="flex items-center gap-2 rounded-full bg-background-100 p-1">
                {(["全部", "连载中", "已完结"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatus(s)}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                      status === s ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-600"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {results.length > 0 ? (
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                {results.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            ) : (
              <div className="mt-6 rounded-lg bg-background-100 py-16 text-center">
                <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
                <p className="mt-3 text-sm text-foreground-600">没有找到匹配的作品，试试更换关键词或筛选条件。</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}